import { Button, Typography, Box, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { FallbackRender } from '@sentry/react';

/**
 * Fallback de nivel de módulo/layout autenticado.
 * Se usa en el Sentry.ErrorBoundary del <Outlet> de InitialLayout.
 * El Header, Sidebar y Footer sobreviven — el usuario puede navegar a otra sección.
 */
const ModuleCrashFallback: FallbackRender = ({ error, resetError, eventId }) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="400px"
      p={3}
      flexGrow={1}
    >
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          p: 4,
          maxWidth: 560,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 52, color: '#ef4444', mb: 2 }} />

        <Typography variant="h6" fontWeight={700} color="text.primary" mb={1}>
          Esta sección no está disponible
        </Typography>

        <Typography variant="body2" color="text.secondary" mb={3}>
          No fue posible mostrar este contenido. Puedes intentar nuevamente o volver al escritorio.
          {eventId && (
            <Box
              component="span"
              display="block"
              mt={1}
              sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#94a3b8' }}
            >
              ID: {eventId}
            </Box>
          )}
        </Typography>

        <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
          <Button
            variant="contained"
            size="small"
            onClick={resetError}
            sx={{ backgroundColor: '#0050da', '&:hover': { backgroundColor: '#0040b0' } }}
          >
            Reintentar
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            onClick={() => window.location.assign('/')}
          >
            Ir al escritorio
          </Button>
        </Box>

        {import.meta.env.DEV && error instanceof Error && (
          <Box
            component="pre"
            sx={{
              mt: 3,
              p: 2,
              overflow: 'auto',
              backgroundColor: '#1e293b',
              color: '#f1f5f9',
              borderRadius: 1,
              fontSize: '0.72rem',
              textAlign: 'left',
            }}
          >
            {error.message}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ModuleCrashFallback;
